import { Typography } from '@mui/material'
import { OptJobType } from 'api/optJobs/optJobs.api'
import React, { ReactElement } from 'react'
import LatestPublishedOptimizationInfoBox from '../LastPublishedOptimizationInfoBox/LatestPublishedOptimizationInfoBox'
import PublishedOptimizationWarningBox from '../LastPublishedOptimizationInfoBox/PublishedOptimizationWarningBox/PublishedOptimizationWarningBox'
import OptimizeWIPTour from '../OptimizeTour/OptimizeWIPTour'
import PublishOptimizationResultButton from '../PublishOptimizationResultButton/PublishOptimizationResultButton'
import AccumulatorLevels from './AccumulatorLevels/AccumulatorLevels'
import ActiveDeviationSettings from './ActiveDeviationSettings/ActiveDeviationSettings'
import AutomaticOptimizationToggleButton from './AutomaticOptimizationToggleButton/AutomaticOptimizationToggleButton'
import FilterModuleContent from './FilterModuleContent/FilterModuleContent'
import LatestSubtypeOptimizationInfoBox from './LatestSubtypeOptimizationInfoBox/LatestSubtypeOptimizationInfoBox'
import OptimizationFailedGuideWrapper from './OptimizationFailedGuideWrapper/OptimizationFailedGuideWrapper'
import OptimizeDateRangeSwitch from './OptimizeDateRangeSwitch/OptimizeDateRangeSwitch'
import { OptModelOptimizationProvider } from './OptModelOptimizationContext'
import ProductionPlanOutOfDate from './ProductionPlanOutOfDate/ProductionPlanOutOfDate'
import StartOptimizationButton from './StartOptimizationButton/StartOptimizationButton'

type OptimizeModuleHeaderProps = {
  title: string
  optJobType: OptJobType
  showAccumulatorLevels?: boolean

  // "the optimize button and alike"
  optimizePermission?: UserPermission
  showOptimizeButton?: boolean
  showAutomaticOptimizationToggleButton?: boolean
  showProductionPlanOutOfDate?: boolean
  showLatestSubtypeOptimizationInfoBox?: boolean
  showOptimizationFailedGuide?: boolean

  showFilterButton?: boolean
  showDateRangeSwitch?: boolean
  showActiveDeviationSettings?: boolean
  showPublishOptimizationButton?: boolean
  showLatestPublishedOptimizationInfoBox?: boolean
  showPublishedOptimizationWarningBox?: boolean
}

// Header component for production plan module, WIP and normal
export default function OptimizeModuleHeader({
  title,
  optJobType,
  optimizePermission,
  showAccumulatorLevels = true,
  showOptimizeButton = true,
  showAutomaticOptimizationToggleButton = true,
  showProductionPlanOutOfDate = true,
  showLatestSubtypeOptimizationInfoBox = true,
  showOptimizationFailedGuide = true,
  showFilterButton = true,
  showDateRangeSwitch = true,
  showActiveDeviationSettings = true,
  showPublishOptimizationButton = true,
  showLatestPublishedOptimizationInfoBox = true,
  showPublishedOptimizationWarningBox = true,
}: OptimizeModuleHeaderProps): ReactElement {
  return (
    <OptModelOptimizationProvider>
      <div
        id="optimize-tours-header" // For tours
        className="tour-target-welcome flex flex-wrap items-start justify-end gap-2 w-full bg-white px-7 py-2 shadow-md relative"
      >
        {/* Title - left aligned. White text is a "space claimer" but will wrap to the end on smaller screens, hence absolute text that is actually shown to be the title to the left. */}
        <Typography variant="h1" className="whitespace-nowrap text-white">
          {title}
        </Typography>
        <Typography variant="h1" data-testid={'header_title'} className="absolute top-2 left-7 z-2">
          {title}
        </Typography>

        {optJobType === 'regular_wip' && (
          <div className="absolute top-8 left-7">
            <OptimizeWIPTour />
          </div>
        )}

        {showDateRangeSwitch && <OptimizeDateRangeSwitch />}

        <div className="flex grow" />

        {/* All controls - right aligned, wrap naturally as screen shrinks */}
        {showFilterButton && <FilterModuleContent portalId="openFilterOptions" />}

        {showAccumulatorLevels && <AccumulatorLevels />}

        {showActiveDeviationSettings && <ActiveDeviationSettings />}

        {showAutomaticOptimizationToggleButton && (
          <AutomaticOptimizationToggleButton optJobType={optJobType} permission={optimizePermission} />
        )}

        {showPublishedOptimizationWarningBox && (
          <PublishedOptimizationWarningBox isVisible={optJobType === 'regular_wip'} />
        )}
        {showProductionPlanOutOfDate && <ProductionPlanOutOfDate optJobType={optJobType} />}

        {showOptimizationFailedGuide && <OptimizationFailedGuideWrapper optJobType={optJobType} />}

        {showLatestSubtypeOptimizationInfoBox && <LatestSubtypeOptimizationInfoBox optJobType={optJobType} />}

        {showOptimizeButton && (
          <div className="tour-target-optimize">
            <StartOptimizationButton optJobType={optJobType} permission={optimizePermission} />
          </div>
        )}

        {showPublishOptimizationButton && (
          <div className="tour-target-publish">
            <PublishOptimizationResultButton />
          </div>
        )}

        {showLatestPublishedOptimizationInfoBox && (
          <LatestPublishedOptimizationInfoBox type={optJobType} isVisible={optJobType === 'regular'} />
        )}

        {/* Filter portal */}
        <div id="openFilterOptions" className="w-full" />
      </div>
    </OptModelOptimizationProvider>
  )
}
